import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Plus, X, Scale } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';

const DOC_LABEL: Record<string, string> = {
  inventory: 'Inventory', valuation: 'Valuation', 'asset-management-plan': 'Asset Mgmt Plan',
  'financial-report': 'Financial Report', policy: 'Policy', strategy: 'Strategy',
  'case-study': 'Case Study', funding: 'Funding', news: 'News', other: 'Other',
};
const label = (k: string) => DOC_LABEL[k] || k;

type CA = { id: string; name: string };
type Entity = { id: string; name: string; total: number; outputTypes: { documentType: string; count: number }[]; topTags: { name: string; count: number }[] };
type Field = { caCount: number; casWithContent: number; avgItems: number; outputTypeAdoption: { documentType: string; caCount: number; pct: number }[] };

export default function Compare() {
  const { homeEntity } = useAuth();
  const [params] = useSearchParams();
  const [cas, setCAs] = useState<CA[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [field, setField] = useState<Field | null>(null);
  const [gaps, setGaps] = useState<{ documentType: string; peerCount: number; peerPct: number }[]>([]);
  const [picker, setPicker] = useState('');

  useEffect(() => {
    axios.get('/api/map/conservation-authorities')
      .then(r => setCAs(r.data.map((c: any) => ({ id: c.id, name: c.name })).sort((a: CA, b: CA) => a.name.localeCompare(b.name))))
      .catch(() => {});
  }, []);

  // Seed initial selection: ?ids= , else the user's home CA.
  useEffect(() => {
    const qids = (params.get('ids') || '').split(',').map(s => s.trim()).filter(Boolean);
    if (qids.length) setSelected(qids.slice(0, 4));
    else if (homeEntity) setSelected([homeEntity.id]);
  }, [params, homeEntity]);

  useEffect(() => {
    if (!selected.length) { setEntities([]); setField(null); setGaps([]); return; }
    axios.get(`/api/entities/compare?ids=${selected.join(',')}&vsField=true`)
      .then(r => { setEntities(r.data.entities); setField(r.data.field); })
      .catch(() => {});
    axios.get(`/api/entities/${selected[0]}/gaps`).then(r => setGaps(r.data.gaps)).catch(() => setGaps([]));
  }, [selected]);

  const add = (id: string) => { if (id && !selected.includes(id) && selected.length < 4) setSelected([...selected, id]); setPicker(''); };
  const remove = (id: string) => setSelected(selected.filter(s => s !== id));

  // Union of output types across the field adoption order (stable, adoption-ranked).
  const typeRows = useMemo(() => {
    const order = (field?.outputTypeAdoption || []).map(a => a.documentType);
    const seen = new Set(order);
    for (const e of entities) for (const o of e.outputTypes) if (!seen.has(o.documentType)) { seen.add(o.documentType); order.push(o.documentType); }
    return order;
  }, [field, entities]);

  const countFor = (e: Entity, type: string) => e.outputTypes.find(o => o.documentType === type)?.count || 0;
  const adoptFor = (type: string) => field?.outputTypeAdoption.find(a => a.documentType === type);

  return (
    <div className="bg-transparent min-h-full p-8">
      <div className="mb-5">
        <h1 className="text-[22px] font-semibold text-[color:var(--text-primary)] m-0 mb-1 inline-flex items-center gap-2"><Scale size={20} /> Benchmark &amp; Compare</h1>
        <p className="text-[13px] text-[color:var(--text-muted)] m-0">Compare conservation authorities side-by-side against each other and the field average.</p>
      </div>

      {/* Selected chips + picker */}
      <div className="flex items-center gap-2 flex-wrap mb-5">
        {selected.map(id => {
          const ca = cas.find(c => c.id === id);
          return (
            <span key={id} className="inline-flex items-center gap-1 text-[12px] px-2 py-1 rounded-full border border-[var(--border)] bg-[var(--surface)] text-[color:var(--text-primary)]">
              {ca?.name || id}
              <button onClick={() => remove(id)} className="text-[color:var(--text-muted)] hover:text-[#f472b6]"><X size={12} /></button>
            </span>
          );
        })}
        {selected.length < 4 && (
          <select value={picker} onChange={e => add(e.target.value)} className="bg-[var(--surface)] border border-[var(--border)] rounded-[8px] px-2 py-[6px] text-[12px] text-[color:var(--text-primary)]">
            <option value="">+ add a CA…</option>
            {cas.filter(c => !selected.includes(c.id)).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
      </div>

      {entities.length > 0 && (
        <div className="bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-[16px] p-5 mb-6 overflow-x-auto">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="text-[color:var(--text-muted)] text-[11px] uppercase tracking-wide">
                <th className="text-left font-medium py-2 pr-4">Output type</th>
                {entities.map(e => <th key={e.id} className="text-center font-medium py-2 px-3 min-w-[90px]">{e.name.replace(/ Conservation Authority$/, '')}</th>)}
                {field && <th className="text-center font-medium py-2 px-3 text-[color:var(--accent-blue)]">Field</th>}
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-[var(--border)]">
                <td className="py-2 pr-4 font-medium text-[color:var(--text-primary)]">Items indexed</td>
                {entities.map(e => <td key={e.id} className="text-center py-2 px-3 text-[color:var(--text-primary)]">{e.total}</td>)}
                {field && <td className="text-center py-2 px-3 text-[color:var(--text-muted)]">{field.avgItems} avg</td>}
              </tr>
              {typeRows.map(type => {
                const ad = adoptFor(type);
                return (
                  <tr key={type} className="border-t border-[var(--border)]">
                    <td className="py-2 pr-4 text-[color:var(--text-secondary)]">{label(type)}</td>
                    {entities.map(e => {
                      const n = countFor(e, type);
                      return <td key={e.id} className={`text-center py-2 px-3 ${n ? 'text-[color:var(--text-primary)]' : 'text-[color:var(--text-muted)] opacity-40'}`}>{n || '—'}</td>;
                    })}
                    {field && <td className="text-center py-2 px-3 text-[color:var(--text-muted)]">{ad ? `${ad.pct}%` : '—'}</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {field && <div className="text-[11px] text-[color:var(--text-muted)] mt-3">Field = all {field.caCount} conservation authorities ({field.casWithContent} with indexed content). “%” = share of CAs that have produced that output type.</div>}
        </div>
      )}

      {/* Gap finder for the first selected CA */}
      {selected.length > 0 && gaps.length > 0 && (
        <div className="bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-[16px] p-5">
          <div className="text-[14px] font-semibold text-[color:var(--text-primary)] mb-1">Opportunities for {cas.find(c => c.id === selected[0])?.name?.replace(/ Conservation Authority$/, '') || 'this CA'}</div>
          <p className="text-[12px] text-[color:var(--text-muted)] mb-3">Output types peers have produced that aren’t indexed for them yet — potential areas to explore.</p>
          <div className="flex flex-col gap-2">
            {gaps.map(g => (
              <div key={g.documentType} className="flex items-center justify-between text-[13px] border-b border-[var(--border)] pb-2 last:border-0">
                <span className="text-[color:var(--text-primary)]">{label(g.documentType)}</span>
                <span className="text-[color:var(--text-muted)]"><span className="text-[color:var(--accent)]">{g.peerCount}</span> peer CA{g.peerCount === 1 ? '' : 's'} ({g.peerPct}%)</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {selected.length > 0 && gaps.length === 0 && entities.length > 0 && (
        <div className="text-[13px] text-[color:var(--text-muted)]">No output-type gaps for {cas.find(c => c.id === selected[0])?.name || 'this CA'} — it has produced every type seen across the field.</div>
      )}
    </div>
  );
}
